import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { API_METHODS, TASK_SORT_BY } from 'constants/general';
import {
  MA_RISK_SECTORS_AFFECTED,
  MATERIALITY_SPECIFIC_STATE,
  MATERIALITY_STAKEHOLDER_SPECIFIC_AI,
  SECTOR_SPECIFIC_CREATE,
  SECTOR_SPECIFIC_CUSTOM_DETAILS,
  SECTOR_SPECIFIC_CUSTOM_LIST,
  SECTOR_SPECIFIC_DETAILS,
  SECTOR_SPECIFIC_LIST, STANDARDS_LIST, STANDARDS_TOPIC_DETAILS, STANDARDS_TOPICS,
  STANDARDS_TOPICS_ADMIN,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import { SPECIFIC_LIST_TAG } from 'store/tags/specificTags';
import { SPECIFIC_CUSTOM_LIST_TAG } from 'store/tags/specificTagsCustom';
import { STANDARDS_LIST_TAG } from 'store/tags/standardsTags';
import { STANDARDS_TOPIC_TAG } from 'store/tags/standardsTopicTags';

import type IStateAI from 'store/slices/materialAssessment/interfaces/IStateAI';
import type IUpdateAIGeoRiskRequest from 'store/slices/materialAssessment/interfaces/IUpdateAIGeoRiskRequest';
import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type ICreateSpecificCustomRequest from '../interfaces/ICreateSpecificCustomRequest';
import type ICreateSpecificRequest from '../interfaces/ICreateSpecificRequest';
import type IDeleteStandardTopicCustomRequest from '../interfaces/IDeleteStandardTopicCustomRequest';
import type IDeleteStandardTopicRequest from '../interfaces/IDeleteStandardTopicRequest';
import type ISectorSpecificItem from '../interfaces/ISectorSpecificItem';
import type IStandardCurrentRequest from '../interfaces/IStandardCurrentRequest';
import type IStandardRequest from '../interfaces/IStandardRequest';
import type IStandardsItem from '../interfaces/IStandardsItem';
import type IStandardTopicsItem from '../interfaces/IStandardTopicsItem';
import type IStandardTopicsItemRequest from '../interfaces/IStandardTopicsItemRequest';
import type IStandardTopicsRequest from '../interfaces/IStandardTopicsRequest';

export const standardsApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    getStandardsList: build.query<
    IBaseQueryPaginationResponse<IStandardsItem[]>, IStandardRequest>({
      query: ({
        offset,
      }) => ({
        url: STANDARDS_LIST,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          limit: DATA_SET_PAGINATION_COUNT,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: STANDARDS_LIST_TAG, id } as const)), STANDARDS_LIST_TAG]
          : [STANDARDS_LIST_TAG]
      ),
    }),
    getStandardTopicsList: build.query<
    IBaseQueryPaginationResponse<IStandardTopicsItem[]>, IStandardTopicsRequest>({
      query: ({
        offset, standardId,
      }) => ({
        url: STANDARDS_TOPICS,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(standardId ? { standardId } : {}),
          orderBy: `-${TASK_SORT_BY.CREATED_AT}`,
          limit: DATA_SET_PAGINATION_COUNT,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: STANDARDS_TOPIC_TAG, id } as const)), STANDARDS_TOPIC_TAG]
          : [STANDARDS_TOPIC_TAG]
      ),
    }),
    deleteStandardTopic: build.mutation<void, IDeleteStandardTopicRequest>({
      query: ({ id }) => ({
        url: STANDARDS_TOPIC_DETAILS.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [STANDARDS_TOPIC_TAG],
    }),
    createStandardTopic: build.mutation<IBaseQueryResponse<IStandardTopicsItem>,
    IStandardTopicsItem>({
      query: (data) => ({
        url: STANDARDS_TOPICS_ADMIN,
        method: API_METHODS.post,
        body: data,
      }),
      invalidatesTags: [STANDARDS_TOPIC_TAG],
    }),
    updateStandardTopic: build.mutation<IBaseQueryResponse<IStandardTopicsItem>,
    IStandardTopicsItemRequest>({
      query: ({ data, id }) => ({
        url: STANDARDS_TOPIC_DETAILS.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body: data,
      }),
      invalidatesTags: [],
    }),
    getSpecificList: build.query<
    IBaseQueryPaginationResponse<ISectorSpecificItem[]>, IStandardRequest>({
      query: ({
        offset, orderBy,
      }) => ({
        url: SECTOR_SPECIFIC_LIST,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(orderBy && orderBy.length > 0 ? { orderBy } : {}),
          limit: DATA_SET_PAGINATION_COUNT,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: SPECIFIC_LIST_TAG, id } as const)), SPECIFIC_LIST_TAG]
          : [SPECIFIC_LIST_TAG]
      ),
    }),
    createSectorSpecificTopic: build.mutation<IBaseQueryResponse<ISectorSpecificItem>,
    ICreateSpecificRequest>({
      query: (data) => ({
        url: SECTOR_SPECIFIC_CREATE,
        method: API_METHODS.post,
        body: data,
      }),
      invalidatesTags: [SPECIFIC_LIST_TAG],
    }),
    deleteSectorSpecificTopic: build.mutation<void, IDeleteStandardTopicRequest>({
      query: ({ id }) => ({
        url: SECTOR_SPECIFIC_DETAILS.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [],
    }),
    getSpecificCustomList: build.query<
    IBaseQueryPaginationResponse<ISectorSpecificItem[]>, IStandardCurrentRequest>({
      query: ({
        offset, maId, orderBy,
      }) => ({
        url: SECTOR_SPECIFIC_CUSTOM_LIST.replace('{maId}', `${maId}`),
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(orderBy && orderBy.length > 0 ? { orderBy } : {}),
          limit: DATA_SET_PAGINATION_COUNT,
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: SPECIFIC_CUSTOM_LIST_TAG, id } as const)), SPECIFIC_CUSTOM_LIST_TAG]
          : [SPECIFIC_CUSTOM_LIST_TAG]
      ),
    }),
    getStateAISpecificRisk: build.query<IBaseQueryResponse<IStateAI>, IUpdateAIGeoRiskRequest>({
      query: ({ maId, vcmId }) => ({
        url: MATERIALITY_SPECIFIC_STATE.replace('{maId}', `${maId}`).replace('{vcmId}', `${vcmId}`),
      }),
    }),
    updateAISpecificRisk: build.mutation<null, IUpdateAIGeoRiskRequest>({
      query: ({ maId }) => ({
        url: MATERIALITY_STAKEHOLDER_SPECIFIC_AI.replace('{maId}', `${maId}`),
        method: API_METHODS.post,
      }),
      invalidatesTags: [SPECIFIC_CUSTOM_LIST_TAG, SPECIFIC_LIST_TAG],
    }),
    refreshAISpecificRisk: build.mutation<null, IUpdateAIGeoRiskRequest>({
      query: ({ maId }) => ({
        url: MA_RISK_SECTORS_AFFECTED.replace('{maId}', `${maId}`),
        method: API_METHODS.post,
      }),
      invalidatesTags: [SPECIFIC_CUSTOM_LIST_TAG, SPECIFIC_LIST_TAG],
    }),
    createSectorCustomSpecificTopic: build.mutation<IBaseQueryResponse<ISectorSpecificItem>,
    ICreateSpecificCustomRequest>({
      query: ({ body, maId }) => ({
        url: SECTOR_SPECIFIC_CUSTOM_LIST.replace('{maId}', `${maId}`),
        method: API_METHODS.post,
        body,
      }),
      invalidatesTags: [SPECIFIC_CUSTOM_LIST_TAG],
    }),
    deleteSectorCustomSpecificTopic: build.mutation<void, IDeleteStandardTopicCustomRequest>({
      query: ({ id, maId }) => ({
        url: SECTOR_SPECIFIC_CUSTOM_DETAILS.replace('{id}', `${id}`).replace('{maId}', `${maId}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [],
    }),
  }),
});

export const {
  useGetStandardsListQuery,
  useGetStandardTopicsListQuery,
  useLazyGetStandardTopicsListQuery,
  useDeleteStandardTopicMutation,
  useCreateStandardTopicMutation,
  useUpdateStandardTopicMutation,
  useGetSpecificListQuery,
  useCreateSectorSpecificTopicMutation,
  useDeleteSectorSpecificTopicMutation,
  useGetSpecificCustomListQuery,
  useCreateSectorCustomSpecificTopicMutation,
  useDeleteSectorCustomSpecificTopicMutation,
  useUpdateAISpecificRiskMutation,
  useGetStateAISpecificRiskQuery,
  useRefreshAISpecificRiskMutation,
} = standardsApi;
